import React from 'react';
import * as helmetModule from 'react-helmet-async';

const Helmet = helmetModule.Helmet || helmetModule.default?.Helmet;

const siteTitle = 'Broadway Muses';
const siteUrl = 'https://broadwaymuses.com';
const homepageTitle = 'Broadway Muses | Stage Stories & Reviews';
const socialImageAlt =
  'Broadway Muses — Broadway culture, stories & conversations from NYC';
const defaultImage = `${siteUrl}/og-image.jpg`;

const pageTitles = {
  '/': homepageTitle,
  '/about': 'About Broadway Muses | Our Theater Voice',
  '/journal': 'Broadway Journal | Stories & Reviews',
  '/contact': 'Contact Broadway Muses | Get in Touch',
  '/privacy': 'Broadway Muses Privacy Policy',
  '/terms': 'Broadway Muses Terms of Service',
  '/ada-statement': 'Broadway Muses Accessibility Statement',
  '/404': 'Broadway Muses | Page Not Found',
};

const SEO = ({
  title,
  description,
  path = '/',
  image,
  indexable = true,
  article,
}) => {
  const fullTitle = pageTitles[path] || (title
    ? `${title} | ${siteTitle}`
    : siteTitle);
  const socialImage = image
    ? image.startsWith('http://') || image.startsWith('https://')
      ? image
      : `${siteUrl}${image.startsWith('/') ? image : `/${image}`}`
    : defaultImage;
  const url = `${siteUrl}${path}`;
  const robots = indexable ? 'index, follow' : 'noindex, follow';

  const pageSchema = {
    '@type': 'WebPage',
    url,
    name: fullTitle,
    description,
    image: socialImage,
    inLanguage: 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      url: `${siteUrl}/`,
      name: siteTitle,
    },
  };

  const organizationSchema = {
    '@type': 'Organization',
    name: siteTitle,
    url: `${siteUrl}/`,
    logo: `${siteUrl}/logo.svg`,
  };

  const articleSchema = article
    ? {
        '@type': 'Article',
        headline: article.headline,
        author: {
          '@type': 'Person',
          name: 'Ashley',
        },
        datePublished: article.datePublished,
        image: article.image,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url,
        },
        publisher: {
          '@type': 'Organization',
          name: siteTitle,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/logo.svg`,
          },
        },
      }
    : null;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      organizationSchema,
      pageSchema,
      ...(articleSchema ? [articleSchema] : []),
    ],
  };

  const structuredDataJson = JSON.stringify(structuredData);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={socialImageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:image:alt" content={socialImageAlt} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structuredDataJson }}
      />
    </Helmet>
  );
};

export default SEO;